import * as React from "react";
import * as PopoverPrimitive from "@radix-ui/react-popover";
import { X } from "lucide-react";
import { useTranslation } from "react-i18next";

import { FOCUS_RING } from "./v2/lib/focus";
import { Popover, PopoverAnchor, PopoverClose, PopoverContent } from "./v2/ui/popover";

/** Bump the suffix to show the introduction again after the flow changes. */
export const CONFIRM_CHANGES_TUTORIAL_KEY = "lytra.tutorial.confirm-changes.v1";

/**
 * The one-time pointer at the confirm button (#820). Venus only.
 *
 * Not a modal: it points at the very button the agent has to press, over the page
 * and without a scrim, so the agent keeps working around it. It takes no focus and
 * clicks elsewhere do not dismiss it; the X, Escape, or pressing the button it
 * points at (`onDismiss`) do. The caller decides when it is `open` (the first
 * button with something to confirm, not yet seen) and remembers the dismissal.
 */
export function ConfirmChangesTutorial({
  open,
  onDismiss,
  children,
}: {
  open: boolean;
  onDismiss: () => void;
  /** The confirm button: the single element the popover is anchored to and points at. */
  children: React.ReactElement;
}) {
  const { t } = useTranslation();
  return (
    <Popover open={open} onOpenChange={(next) => !next && onDismiss()}>
      <PopoverAnchor asChild>{children}</PopoverAnchor>
      <PopoverContent
        side="top"
        align="end"
        width="md"
        role="dialog"
        aria-label={t("confirmTutorial.title", "Änderungen bestätigen")}
        // Brand Deep Blue with white text, the pairing DESIGN.md names for light type,
        // so the pointer stands out from the white page and cards around it.
        className="border-primary bg-primary text-primary-foreground"
        // Not modal and not focus-stealing: the agent is mid-task on the page.
        onOpenAutoFocus={(event) => event.preventDefault()}
        onInteractOutside={(event) => event.preventDefault()}
      >
        {/* A plain button, not the v2 IconButton: that one needs a TooltipProvider the apps do not mount. */}
        <PopoverClose asChild>
          <button
            type="button"
            aria-label={t("confirmTutorial.close", "Schließen")}
            className={`absolute right-2 top-2 inline-flex size-6 items-center justify-center rounded-md text-primary-foreground transition-colors hover:bg-primary-hover active:bg-primary-active ${FOCUS_RING}`}
          >
            <X aria-hidden className="size-4" strokeWidth={1.75} />
          </button>
        </PopoverClose>
        <p className="pr-6 text-label text-primary-foreground">{t("confirmTutorial.title", "Änderungen bestätigen")}</p>
        <p className="mt-1 text-body text-primary-foreground">
          {t(
            "confirmTutorial.body",
            "Prüfen Sie die Fakten und klicken Sie auf „Bestätigen“. Danach erscheint die Antwort. Ein Klick auf „Bestätigt“ nimmt die Bestätigung zurück.",
          )}
        </p>
        <PopoverPrimitive.Arrow className="fill-primary" width={14} height={7} />
      </PopoverContent>
    </Popover>
  );
}
